import React, { useEffect, useState, useRef, useCallback } from "react";
import { Title, Meta, Link as HeadLink } from "react-head";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { FiSearch, FiX, FiImage, FiEye, FiCheck, FiVideo } from "react-icons/fi";

import ClientCalling from "../api/ClientCalling";
import SmartImage from "../components/Client/SmartImage";
import FixedButton from "../components/Client/FixedButton";
import InpixFooter from "../components/Client/InpixFooter";
import ClientAdSense from "../components/Client/ClientAdSense";
import { formatView } from "../components/formatView";

const PAGE_LIMIT = 18;

/* ---------- Skeleton ---------- */
const CardSkeleton = () => (
    <div className="relative aspect-[9/16] rounded-2xl ios-shimmer overflow-hidden">
        <div className="absolute top-2 left-2 w-10 h-4 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
        <div className="absolute bottom-2 left-2 right-2 h-4 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
    </div>
);

/* ---------- Category chip (wallpaper-style) ---------- */
const Chip = ({ active, label, count, icon, onClick }) => (
    <motion.button
        whileTap={{ scale: 0.94 }}
        onClick={onClick}
        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] md:text-xs font-semibold transition-colors"
        style={
            active
                ? { color: "#fff", background: "linear-gradient(135deg, #7F00FF, #FF8AFF)", boxShadow: "0 8px 20px -8px rgba(127,0,255,0.6)" }
                : { color: "rgba(255,255,255,0.8)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }
        }
    >
        {icon}
        <span className="whitespace-nowrap">{label}</span>
        {count != null && (
            <span className="tabular-nums text-[9px] md:text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ background: active ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)" }}>
                {count}
            </span>
        )}
    </motion.button>
);

/* ---------- Template (status) card ---------- */
const TemplateCard = ({ item, index = 0, onOpen }) => {
    const isVideo = item.i_type === "video";
    // Video is loaded ONLY while hovered → no bandwidth spent until the user previews it.
    const [hover, setHover] = useState(false);

    return (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: Math.min(index * 0.025, 0.3), ease: [0.22, 1, 0.36, 1] }}
        whileTap={{ scale: 0.97 }}
        onClick={() => onOpen(item)}
        onMouseEnter={() => isVideo && setHover(true)}
        onMouseLeave={() => isVideo && setHover(false)}
        className="relative aspect-[9/16] rounded-2xl cursor-pointer group bg-client-back"
        style={{ overflow: "clip", boxShadow: "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)" }}
    >
        <SmartImage
            src={item.i_thumbnail || item.i_media}
            alt={item.i_name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {/* hover-only video preview — mounts on hover (loads), unmounts on leave (frees bandwidth) */}
        {isVideo && hover && (
            <video
                src={item.i_media}
                muted
                loop
                autoPlay
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
            />
        )}
        <div className="absolute inset-x-0 bottom-0 h-3/5 pointer-events-none"
            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 45%, transparent 100%)" }} />
        <div className="absolute top-2 left-2 flex items-center gap-1.5 pointer-events-none">
            <span className="ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium">
                <FiEye className="text-[10px] md:text-xs" />
                <span className="tabular-nums">{formatView(item.i_view_count || 0)}</span>
            </span>
            {item.i_type === "video" && (
                <span className="ios-glass-dark px-1.5 py-0.5 rounded-full flex items-center text-white">
                    <FiVideo className="text-[10px] md:text-xs" />
                </span>
            )}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-2.5 md:p-3">
            <p className="text-white text-[11px] md:text-sm font-semibold truncate" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}>
                {item.i_name}
            </p>
            {item.i_category_name && (
                <p className="text-white/60 text-[9px] md:text-[10px] truncate mt-0.5">{item.i_category_name}</p>
            )}
        </div>
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300"
            style={{ boxShadow: "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)" }} />
    </motion.div>
    );
};

const ClientInpix = () => {
    const navigate = useNavigate();
    const { slug: routeSlug } = useParams();
    const activeSlug = routeSlug || ""; // "" = All  (driven by the URL: /inpix or /inpix/category/:slug)
    const observerTarget = useRef(null);
    const searchRef = useRef(null);

    // category chips
    const [cats, setCats] = useState([]);

    // feed
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [nextPage, setNextPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // search (in-place filter)
    const [searching, setSearching] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    // latest filters for the infinite-scroll loader without re-binding it
    const filterRef = useRef({ slug: "", q: "" });
    filterRef.current = { slug: activeSlug, q: search };

    /* dark scrollbar theme like the wallpaper UI */
    useEffect(() => {
        const el = document.documentElement;
        el.classList.add("wp-scroll");
        return () => el.classList.remove("wp-scroll");
    }, []);

    useEffect(() => { if (searching) searchRef.current?.focus(); }, [searching]);

    // Debounce search → reset feed to page 1
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Load category chips once
    useEffect(() => {
        (async () => {
            const resp = await ClientCalling.getInpixCategoryClient(1, 100, "");
            if (resp?.status) setCats(Array.isArray(resp.data) ? resp.data : []);
        })();
    }, []);

    const load = useCallback(async (pageNum, slug, q) => {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);
        try {
            const opts = { sortBy: "createdAt", order: "desc" };
            if (q) opts.search = q;
            const resp = slug
                ? await ClientCalling.getInpixByCategoryClient(slug, pageNum, PAGE_LIMIT, opts)
                : await ClientCalling.getInpixListClient(pageNum, PAGE_LIMIT, opts);

            if (resp?.status) {
                const items = Array.isArray(resp.data) ? resp.data : [];
                setList((prev) => (pageNum === 1 ? items : [...prev, ...items]));
                setNextPage(resp.nextPage || null);
                setTotal(resp.totalRecords || 0);
            } else if (pageNum === 1) {
                setList([]); setNextPage(null); setTotal(0);
            }
        } catch (e) {
            if (pageNum === 1) { setList([]); setNextPage(null); setTotal(0); }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    // Filter changed (chip / search) → reset & load page 1
    useEffect(() => {
        setList([]); setNextPage(null); setPage(1);
        load(1, activeSlug, search);
    }, [activeSlug, search, load]);

    // Page advanced via infinite scroll
    useEffect(() => {
        if (page > 1) load(page, filterRef.current.slug, filterRef.current.q);
    }, [page, load]);

    // Infinite scroll observer
    useEffect(() => {
        const el = observerTarget.current;
        if (!el || !nextPage) return;
        const obs = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting && nextPage && !loading && !loadingMore) setPage((p) => p + 1); },
            { rootMargin: "400px", threshold: 0 }
        );
        obs.observe(el);
        return () => obs.unobserve(el);
    }, [nextPage, loading, loadingMore]);

    const closeSearch = () => { setSearching(false); setSearchInput(""); };
    const openTemplate = (item) => navigate(`/inpix/view/${item.i_slug}`);

    const activeCat = cats.find((c) => c.i_slug === activeSlug);
    const headingTitle = activeSlug ? (activeCat?.i_name || "Status") : "All Status";

    return (
        <div className="min-h-screen dm-sans client-home-bg">
            <Title>{activeSlug ? `${headingTitle} Status` : "Status"} | PicPromptify</Title>
            <Meta name="description" content="Create your own status, posters and quotes — pick a design, add your name and photo, and share. Free on PicPromptify." />
            <Meta property="og:title" content={`${activeSlug ? `${headingTitle} Status` : "Status"} | PicPromptify`} />
            <Meta property="og:type" content="website" />
            <HeadLink rel="canonical" href={`https://picpromptify.com/inpix${activeSlug ? `/category/${activeSlug}` : ""}`} />

            <FixedButton />

            {/* ===== Sticky category bar (wallpaper-style) ===== */}
            <div className="wp-catbar sticky top-0 z-40">
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
                    <div className="flex items-center gap-2 py-3">
                        {searching ? (
                            <div className="flex items-center gap-2 w-full">
                                <button className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white"
                                    style={{ background: "linear-gradient(135deg, #7F00FF, #FF8AFF)" }} aria-label="Search">
                                    <FiSearch className="text-sm" />
                                </button>
                                <input
                                    ref={searchRef}
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Escape") closeSearch(); }}
                                    placeholder="Search status…"
                                    className="flex-1 min-w-0 bg-transparent text-client-font placeholder:text-client-font/40 text-sm md:text-base focus:outline-none"
                                    style={{ caretColor: "#FF8AFF" }}
                                />
                                <button onClick={closeSearch}
                                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-client-font/80"
                                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} aria-label="Close search">
                                    <FiX className="text-base" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <button onClick={() => setSearching(true)}
                                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-client-font/85"
                                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} aria-label="Search status">
                                    <FiSearch className="text-sm" />
                                </button>

                                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-1">
                                    <Chip active={activeSlug === ""} icon={<FiImage className="text-xs" />} label="All" onClick={() => navigate("/inpix")} />
                                    {cats.map((c) => (
                                        <Chip key={c._id} active={activeSlug === c.i_slug} label={c.i_name} count={c.i_count} onClick={() => navigate(`/inpix/category/${c.i_slug}`)} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ===== Feed ===== */}
            <section className="relative w-full px-4 sm:px-6 md:px-8 pt-5 md:pt-7 pb-12 md:pb-20">
                <div className="mx-auto max-w-7xl">
                    {/* top banner ad */}
                    <ClientAdSense slot='9729451986' containerClassName='w-full mb-5 md:mb-7' />

                    {/* heading row */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between gap-3 mb-5 md:mb-7">
                        <div className="flex items-center gap-2">
                            <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #7F00FF, #FCFA02)" }} />
                            <h1 className="text-client-font font-semibold text-sm md:text-lg tracking-wide uppercase">
                                {search ? `Results for “${search}”` : headingTitle}
                            </h1>
                        </div>
                        {!loading && total > 0 && (
                            <div className="ios-glass px-3 py-1 rounded-full text-client-font/75 text-[10px] md:text-xs flex-shrink-0">
                                <span className="tabular-nums font-bold text-client-title">{total}</span> designs
                            </div>
                        )}
                    </motion.div>

                    {/* skeleton */}
                    {loading && page === 1 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                            {[...Array(12)].map((_, i) => <CardSkeleton key={i} />)}
                        </div>
                    )}

                    {/* empty */}
                    {!loading && list.length === 0 && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center text-center py-16 md:py-24">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))", border: "1px solid rgba(255,255,255,0.12)" }}>
                                <FiImage className="text-2xl md:text-3xl text-client-title" />
                            </div>
                            <h3 className="text-client-font font-bold text-lg md:text-2xl tracking-tight">
                                {search ? "No designs found" : "Nothing here yet"}
                            </h3>
                            <p className="text-client-font/55 text-sm md:text-base mt-2 max-w-md">
                                {search ? "Try a different search term or category." : "Check back soon for fresh status designs."}
                            </p>
                        </motion.div>
                    )}

                    {/* grid */}
                    {list.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                            {list.map((item, idx) => (
                                <TemplateCard key={item._id} item={item} index={idx} onOpen={openTemplate} />
                            ))}
                            {loadingMore && [...Array(6)].map((_, i) => <CardSkeleton key={`s-${i}`} />)}
                        </div>
                    )}

                    {/* infinite-scroll sentinel */}
                    {nextPage && (
                        <div ref={observerTarget} className="flex justify-center items-center py-8 md:py-12">
                            {loadingMore && (
                                <div className="ios-glass-dark inline-flex items-center gap-3 px-5 py-3 rounded-full">
                                    <div className="w-4 h-4 rounded-full border-2 border-client-font/30 border-t-client-primary animate-spin" />
                                    <span className="text-client-font text-xs md:text-sm font-medium tracking-wide">Loading more</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* end */}
                    {!nextPage && !loading && list.length > 0 && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-2 py-10 text-client-font/40">
                            <FiCheck className="text-2xl" style={{ color: "#7F00FF" }} />
                            <p className="text-xs md:text-sm tracking-wider uppercase">You've seen it all</p>
                        </motion.div>
                    )}
                </div>
            </section>

            <InpixFooter />
        </div>
    );
};

export default ClientInpix;
